import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import PublicLayout from '@/components/PublicLayout';
import SEO from '@/components/SEO';
import { BlogPostingSchema, BreadcrumbSchema } from '@/components/JsonLd';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import SocialShare from '@/components/SocialShare';
import { Calendar, User, ArrowLeft, Tag } from 'lucide-react';

const BlogDetailPage = () => {
  const { blogId } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blogs/${blogId}`)
      .then(res => setBlog(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [blogId]);

  if (loading) return (
    <PublicLayout>
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    </PublicLayout>
  );

  if (!blog) return (
    <PublicLayout>
      <div className="text-center py-20">
        <h2 className="text-xl font-bold text-slate-700">Article not found</h2>
        <Link to="/blogs"><Button variant="outline" className="mt-4"><ArrowLeft className="w-4 h-4 mr-2" />Back to Blog</Button></Link>
      </div>
    </PublicLayout>
  );

  return (
    <PublicLayout>
      <SEO
        title={blog?.title}
        description={blog?.excerpt || blog?.content?.replace(/<[^>]*>/g, '').slice(0, 160)}
        path={`/blogs/${blogId}`}
        image={blog?.cover_image}
        type="article"
      />
      {blog && <BlogPostingSchema blog={blog} />}
      {blog && <BreadcrumbSchema items={[{ name: 'Blog', url: '/blogs' }, { name: blog.title }]} />}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="blog-detail">
        <Link to="/blogs" className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        <div className="flex items-center gap-3 mb-4">
          <Badge variant="outline">{blog.category || 'general'}</Badge>
          <span className="text-sm text-slate-400 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" />
            {new Date(blog.created_at).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' })}
          </span>
        </div>

        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }} data-testid="blog-title">
          {blog.title}
        </h1>

        <div className="flex items-center justify-between gap-4 text-sm text-slate-500 mb-8 flex-wrap">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4" />
            <span>{blog.author_name || 'EdAssist'}</span>
          </div>
          <SocialShare title={blog.title} text={blog.excerpt || blog.title} label="" />
        </div>

        {blog.cover_image && (
          <div className="aspect-[16/9] rounded-xl overflow-hidden mb-8">
            <img src={blog.cover_image} alt={blog.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div
          className="prose prose-slate max-w-none text-slate-700 leading-relaxed"
          data-testid="blog-content"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        {blog.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t">
            <Tag className="w-4 h-4 text-slate-400" />
            {blog.tags.map(tag => (
              <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
            ))}
          </div>
        )}
      </article>
    </PublicLayout>
  );
};

export default BlogDetailPage;
